// Talk.ai — lista de agentes de IA (/prompts). Mockup "Tela Aeros Dark -
// Talk.ai e Modais": header 60px com busca e "Novo agente", faixa de KPI,
// card de tabela em CSS grid e a nota do rodapé.
import React, { useContext, useEffect, useMemo, useReducer, useState } from "react";

import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import ForumIcon from "@material-ui/icons/Forum";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import GraphicEqIcon from "@material-ui/icons/GraphicEq";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import AllInclusiveIcon from "@material-ui/icons/AllInclusive";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";

import MainContainer from "../../components/MainContainer";
import PromptModal from "../../components/PromptModal";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import usePromptPageStyles from "./promptPageStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { useTheme, alpha } from "@material-ui/core/styles";
import {
  agentTile,
  knowledgeState,
  knowledgeLabel,
  toneLabel,
  voiceShortName,
  usesVoice,
  filesSummary,
  vinculoResumo,
  vinculoTexto,
} from "./promptRules";

const TABLE_GRID = "2.2fr 1.6fr 1.1fr 1fr 1.3fr 110px";

// Ícones dos ladrilhos, na mesma ordem de `agentTile` em promptRules.
const TILE_ICONS = {
  support_agent: SupportAgentIcon,
  storefront: StorefrontIcon,
  receipt_long: ReceiptLongIcon,
  call_split: CallSplitIcon,
  home_repair_service: HomeRepairServiceIcon,
};

const reducer = (state, action) => {
  if (action.type === "LOAD_PROMPTS") {
    const prompts = action.payload;
    const novos = [];
    prompts.forEach((prompt) => {
      const idx = state.findIndex((p) => p.id === prompt.id);
      if (idx !== -1) state[idx] = prompt;
      else novos.push(prompt);
    });
    return [...state, ...novos];
  }

  if (action.type === "UPDATE_PROMPTS") {
    const prompt = action.payload;
    const idx = state.findIndex((p) => p.id === prompt.id);
    if (idx !== -1) {
      state[idx] = prompt;
      return [...state];
    }
    return [prompt, ...state];
  }

  if (action.type === "DELETE_PROMPT") {
    return state.filter((p) => p.id !== action.payload);
  }

  if (action.type === "RESET") return [];

  return state;
};

const Prompts = () => {
  const c = useCampaignPageStyles({ grid: TABLE_GRID });
  const classes = usePromptPageStyles();
  const theme = useTheme();
  const history = useHistory();

  const [prompts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { user, socket } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const companyId = user.companyId;

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useOpenAi) {
        toast.error(i18n.t("aeros.talkai.noPermission"));
        setTimeout(() => history.push("/"), 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A busca é servida pelo backend (`searchParam` do ListPromptsService); o
  // debounce evita uma requisição por tecla.
  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/prompt", { params: { searchParam } });
        dispatch({ type: "RESET" });
        dispatch({ type: "LOAD_PROMPTS", payload: data.prompts });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchParam]);

  useEffect(() => {
    if (!socket) return undefined;
    const onPromptEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_PROMPTS", payload: data.prompt });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_PROMPT", payload: data.promptId });
      }
    };
    socket.on(`company-${companyId}-prompt`, onPromptEvent);
    return () => socket.off(`company-${companyId}-prompt`, onPromptEvent);
  }, [socket, companyId]);

  const handleDelete = async () => {
    if (!selectedPrompt) return;
    setDeleting(true);
    try {
      await api.delete(`/prompt/${selectedPrompt.id}`);
      toast.success(i18n.t("aeros.talkai.delete.done"));
      setConfirmOpen(false);
      setSelectedPrompt(null);
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(false);
    }
  };

  const titleCount = useMemo(() => {
    if (loading && prompts.length === 0) return "";
    return prompts.length === 1
      ? i18n.t("aeros.talkai.countOne")
      : i18n.t("aeros.talkai.count", { count: prompts.length });
  }, [prompts.length, loading]);

  const renderKnowledge = (prompt) => {
    const arquivos = prompt.files || [];
    const estado = knowledgeState(arquivos);
    if (estado === "none") {
      return <span className={c.cellMuted}>{knowledgeLabel(arquivos)}</span>;
    }
    const ehProcessando = estado === "processing";
    return (
      <span
        className={`${c.statusPill} ${ehProcessando ? c.toneWarning : c.toneSuccess}`}
      >
        {ehProcessando ? <HourglassEmptyIcon /> : <MenuBookIcon />}
        {knowledgeLabel(arquivos)}
      </span>
    );
  };

  // Coluna "Conexão · Filas": a conexão em cima e as filas (com a cor de
  // cada uma) embaixo. Agente legado sem conexão ganha o selo "Conexão
  // pendente" — ele precisa ser editado para escolher uma.
  const renderVinculo = (prompt) => {
    const r = vinculoResumo(prompt);
    const filas = r.filas.map((q) => q.name);
    if (r.antesDaFila) filas.push(i18n.t("aeros.talkai.vinculo.preQueue"));
    return (
      <div style={{ minWidth: 0 }}>
        {r.pendente ? (
          <Tooltip title={i18n.t("aeros.talkai.vinculo.pendingHint")}>
            <span className={`${c.statusPill} ${c.toneWarning}`}>
              <LinkOffIcon />
              {i18n.t("aeros.talkai.vinculo.pending")}
            </span>
          </Tooltip>
        ) : (
          <div className={c.cellEllipsis} title={r.conexao}>
            {r.conexao || "—"}
          </div>
        )}
        {filas.length > 0 && (
          <span
            className={c.nameSub}
            style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 2 }}
            title={vinculoTexto(prompt)}
          >
            {r.filas[0] && (
              <span
                className={c.statusDot}
                style={{ backgroundColor: r.filas[0].color || AEROS.cyan }}
              />
            )}
            <span className={c.cellEllipsis}>{filas.join(", ")}</span>
          </span>
        )}
      </div>
    );
  };

  const renderRow = (prompt) => {
    const tile = agentTile(prompt.id);
    const TileIcon = TILE_ICONS[tile.icon] || SupportAgentIcon;
    const temVoz = usesVoice(prompt.voice);
    return (
      <div
        key={prompt.id}
        className={`${c.tableRow} ${c.tableRowClickable}`}
        onClick={() => {
          setSelectedPrompt(prompt);
          setPromptModalOpen(true);
        }}
      >
        <div className={c.nameCell}>
          <span
            className={classes.agentTile}
            style={{
              backgroundColor: alpha(tile.color, 0.14),
              color: stateText(theme, tile.color, ""),
            }}
          >
            <TileIcon />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className={c.nameText} title={prompt.name}>
              {prompt.name}
            </div>
            <div className={c.nameSub}>
              {[prompt.agentName, toneLabel(prompt.tone)].filter(Boolean).join(" · ")}
              {temVoz && (
                <span className={classes.voiceTag} style={{ marginLeft: 6 }}>
                  <GraphicEqIcon />
                  {voiceShortName(prompt.voice)}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className={c.tableCell}>{renderVinculo(prompt)}</div>

        <div className={`${c.tableCell} ${classes.modelCell}`}>
          {prompt.model || "—"}
        </div>

        <div className={`${c.tableCell} ${classes.tokensCell}`}>{prompt.maxTokens}</div>

        <div className={c.tableCell}>{renderKnowledge(prompt)}</div>

        <div className={c.cellActions} onClick={(e) => e.stopPropagation()}>
          <Tooltip title={i18n.t("aeros.talkai.actions.simulate")}>
            <IconButton
              className={c.rowActionButton}
              aria-label={i18n.t("aeros.talkai.actions.simulate")}
              onClick={() => {
                setSelectedPrompt({ ...prompt, initialTab: 0 });
                setPromptModalOpen(true);
              }}
            >
              <ForumIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={i18n.t("aeros.talkai.actions.edit")}>
            <IconButton
              className={c.rowActionButton}
              aria-label={i18n.t("aeros.talkai.actions.edit")}
              onClick={() => {
                setSelectedPrompt(prompt);
                setPromptModalOpen(true);
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={i18n.t("aeros.talkai.actions.delete")}>
            <IconButton
              className={`${c.rowActionButton} ${c.rowActionButtonDanger}`}
              aria-label={i18n.t("aeros.talkai.actions.delete")}
              onClick={() => {
                setSelectedPrompt(prompt);
                setConfirmOpen(true);
              }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  };

  // O texto da confirmação nomeia a conexão e as filas que ficam sem
  // atendimento automático e quantos arquivos vão junto — "tem certeza?" não
  // diz o que se perde.
  const deleteDescription = () => {
    if (!selectedPrompt) return "";
    const vinculo = vinculoTexto(selectedPrompt);
    const { ready, processing } = filesSummary(selectedPrompt.files || []);
    const total = ready + processing;
    return total > 0
      ? i18n.t("aeros.talkai.delete.bodyFiles", { vinculo, count: total })
      : i18n.t("aeros.talkai.delete.body", { vinculo });
  };

  return (
    <MainContainer>
      <div className={c.pageHeader}>
        <div>
          <div className={c.pageTitle}>
            {i18n.t("aeros.talkai.title")}
            {titleCount && <span className={c.pageTitleCount}> · {titleCount}</span>}
          </div>
          <div className={classes.pageSubtitle}>{i18n.t("aeros.talkai.subtitle")}</div>
        </div>
        <div className={c.headerActions}>
          <InputBase
            className={c.searchPill}
            placeholder={i18n.t("aeros.talkai.search")}
            type="search"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            startAdornment={<SearchIcon />}
            inputProps={{ "aria-label": i18n.t("aeros.talkai.search") }}
          />
          <Button
            variant="contained"
            color="primary"
            className={c.gradientButton}
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedPrompt(null);
              setPromptModalOpen(true);
            }}
          >
            {i18n.t("aeros.talkai.newAgent")}
          </Button>
        </div>
      </div>

      <div className={c.pageBody}>
        <div className={classes.kpiGrid}>
          <div className={classes.kpiCard}>
            <span className={classes.kpiTile}>
              <AllInclusiveIcon />
            </span>
            <div style={{ minWidth: 0 }}>
              <div className={classes.kpiValue}>{prompts.length}</div>
              <div className={classes.kpiLabel}>
                {i18n.t("aeros.talkai.kpi.activeAgents")}
              </div>
            </div>
          </div>
        </div>

        <div className={c.tableCard}>
          {loading && prompts.length === 0 ? (
            <div className={c.loadingRow}>…</div>
          ) : prompts.length === 0 ? (
            <div className={c.emptyState}>
              <span className={c.emptyIcon}>
                <AllInclusiveIcon />
              </span>
              <div className={c.emptyTitle}>{i18n.t("aeros.talkai.empty.title")}</div>
              <div className={c.emptyText}>{i18n.t("aeros.talkai.empty.text")}</div>
            </div>
          ) : (
            // Cabeçalho e linhas no MESMO wrapper com scroll horizontal: em
            // tela estreita a tabela rola em vez de o card cortar colunas.
            <div className={classes.tableScroll}>
              <div className={classes.tableInner}>
                <div className={c.tableHeadRow}>
                  <div className={c.tableHeadCell}>{i18n.t("aeros.talkai.table.agent")}</div>
                  <div className={c.tableHeadCell}>{i18n.t("aeros.talkai.table.vinculo")}</div>
                  <div className={c.tableHeadCell}>{i18n.t("aeros.talkai.table.model")}</div>
                  <div className={c.tableHeadCell}>
                    {i18n.t("aeros.talkai.table.maxTokens")}
                  </div>
                  <div className={c.tableHeadCell}>
                    {i18n.t("aeros.talkai.table.knowledge")}
                  </div>
                  <div className={`${c.tableHeadCell} ${c.cellActionsHead}`}>
                    {i18n.t("aeros.talkai.table.actions")}
                  </div>
                </div>
                {prompts.map(renderRow)}
              </div>
            </div>
          )}
        </div>

        <div className={classes.footerNote}>
          <InfoOutlinedIcon />
          {i18n.t("aeros.talkai.footerNote")}
        </div>
      </div>

      <PromptModal
        open={promptModalOpen}
        onClose={() => {
          setPromptModalOpen(false);
          setSelectedPrompt(null);
        }}
        promptId={selectedPrompt?.id}
        initialTab={selectedPrompt?.initialTab}
      />

      <AerosConfirmModal
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setSelectedPrompt(null);
        }}
        onConfirm={handleDelete}
        loading={deleting}
        icon={<DeleteForeverIcon />}
        title={i18n.t("aeros.talkai.delete.title", {
          name: selectedPrompt?.name || "",
        })}
        description={deleteDescription()}
        confirmLabel={i18n.t("aeros.talkai.delete.confirm")}
      />
    </MainContainer>
  );
};

export default Prompts;
